'use client';

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'next/navigation';
import { LangLink } from '../../../i18n/links';
import ScrollLink from '../../Helpers/ScrollLink';
import { defaultLang, SUPPORTED } from '../../../config/languages';
import { formatUsd, getPriceBreakdown } from '../../../config/pricing';
import { getAffiliateCodeFromCurrentUrl, getAppDownloadUrl } from '../../../lib/appDownloadLink';

const FEATURE_ROWS = [
  ['palette', 'feature_canva_import', 'redesign.feature_canva_import_description'],
  ['layer-group', 'feature_layout_editor', 'redesign.feature_layout_editor_description'],
  ['calendar-alt', 'feature_scheduling', 'redesign.feature_scheduling_description'],
  ['tv', 'feature_kiosk_mode', 'redesign.feature_kiosk_mode_description'],
  ['cloud', 'redesign.feature_storage_title', 'redesign.feature_storage_description'],
  ['satellite-dish', 'redesign.feature_remote_title', 'redesign.feature_remote_description'],
];

const PLAN_BENEFITS = [
  'feature_device', 'feature_remote', 'feature_layout_editor', 'feature_kiosk_mode',
  'feature_storage', 'feature_scheduling', 'feature_canva_import', 'redesign.all_features',
];

const TRUST_ITEMS = [
  ['users', 'trust_businesses_title', 'trust_businesses_description'],
  ['shield-alt', 'trust_security_title', 'trust_security_description'],
  ['headset', 'trust_support_title', 'trust_support_description'],
];

export default function PricingLayout2() {
  const [billing, setBilling] = useState('monthly');
  const [displays, setDisplays] = useState(1);
  const { t } = useTranslation('pricing');
  const { lang = defaultLang } = useParams();
  const activeLang = SUPPORTED.includes(lang) ? lang : defaultLang;
  const isYearly = billing === 'yearly';
  const pricing = getPriceBreakdown(displays, billing);
  const setDisplaysClamped = (value) => setDisplays(Math.max(1, Number.isFinite(value) ? value : 1));
  const displayCount = t('redesign.display_count', { count: pricing.displays });
  const pricePeriod = isYearly ? t('redesign.per_year') : t('redesign.per_month');
  const billingSummary = isYearly
    ? t('redesign.yearly_summary', { count: pricing.displays, price: formatUsd(pricing.yearlyMonthlyEquivalent) })
    : pricing.displays === 1
      ? t('redesign.monthly_summary_single', { price: formatUsd(pricing.monthlyTotal) })
      : t('redesign.monthly_summary_multiple', { count: pricing.displays });

  return (
    <>
      <link rel="stylesheet" href="/assets/css/blog-layout-2.css" />
      <link rel="stylesheet" href="/assets/css/content-layout-2.css" />
      <link rel="stylesheet" href="/assets/css/pricing-layout-2.css" />
      <main className="blog-v2-page pricing-v2-page">
        <section className="pricing-v2-hero" aria-labelledby="pricing-page-title">
          <div className="container blog-v2-container">
            <div className="pricing-v2-hero-copy">
              <h1 id="pricing-page-title">{t('redesign.hero_title')}</h1>
              <p className="pricing-v2-hero-highlight">{t('redesign.hero_trial')}</p>
              <p className="pricing-v2-intro">{t('redesign.hero_supporting')}</p>
              <ul className="pricing-v2-trial-benefits" aria-label={t('redesign.trial_benefits_label')}>
                {['trial_benefit_days', 'trial_benefit_features', 'trial_benefit_card'].map((key) => (
                  <li key={key}><i className="fas fa-check" aria-hidden="true" />{t(`redesign.${key}`)}</li>
                ))}
              </ul>
            </div>
            <div className="pricing-v2-side-note pricing-v2-side-note--left" aria-hidden="true">
              <span>{t('redesign.side_note_left')}</span>
              <svg viewBox="0 0 116 70" focusable="false"><path d="M11 8c7 28 30 42 72 42" /><path d="m72 39 13 12-15 9" /></svg>
            </div>
            <div className="pricing-v2-side-note pricing-v2-side-note--right" aria-hidden="true">
              <span>{t('redesign.side_note_right')}</span>
              <svg viewBox="0 0 116 74" focusable="false"><path d="M103 8c10 29-4 51-42 55" /><path d="m72 52-12 11 15 7" /></svg>
            </div>
          </div>
        </section>

        <section className="pricing-v2-plans" aria-label={t('title')}>
          <div className="container blog-v2-container">
            <div className="pricing-v2-controls">
              <div className="pricing-v2-billing-wrap">
                <div className="pricing-v2-toggle" role="group" aria-label={`${t('monthly_plan')} / ${t('yearly_plan')}`}>
                  <button type="button" className={!isYearly ? 'is-active' : ''} aria-pressed={!isYearly} onClick={() => setBilling('monthly')}>{t('monthly_plan')}</button>
                  <button type="button" className={isYearly ? 'is-active' : ''} aria-pressed={isYearly} onClick={() => setBilling('yearly')}>{t('yearly_plan')}</button>
                </div>
                <span className="pricing-v2-savings-badge"><i className="fas fa-tags" aria-hidden="true" />{t('redesign.save_two_months')}</span>
              </div>
              <div className="pricing-v2-stepper-wrap">
                <span>{t('displays_label')}</span>
                <div className="pricing-v2-stepper">
                  <button type="button" aria-label={t('redesign.decrease_displays')} onClick={() => setDisplaysClamped(displays - 1)}>−</button>
                  <input aria-label={t('displays_label')} type="number" inputMode="numeric" min="1" value={displays} onChange={(event) => setDisplaysClamped(Number.parseInt(event.target.value, 10))} />
                  <button type="button" aria-label={t('redesign.increase_displays')} onClick={() => setDisplaysClamped(displays + 1)}>+</button>
                </div>
              </div>
            </div>

            <div className="pricing-v2-grid">
              <article className="pricing-v2-price-card">
                <div className="pricing-v2-plan-heading"><h2>{displayCount}</h2><p>{t('redesign.perfect_to_start')}</p></div>
                <aside className="pricing-v2-trial-callout" aria-label={t('redesign.trial_callout_label')}>
                  <span><i className="fas fa-gift" aria-hidden="true" /></span>
                  <div><strong>{t('redesign.trial_callout_title')}</strong><small>{t('redesign.trial_callout_description')}</small></div>
                </aside>
                <div className="pricing-v2-price"><span>{t('currency')}</span><b>{formatUsd(pricing.total)}</b><em>{pricePeriod}</em></div>
                <p className="pricing-v2-billing">{isYearly ? t('billed_annually') : t('billed_monthly')} · {billingSummary}</p>
                {isYearly && <p className="pricing-v2-yearly-saving">{t('redesign.yearly_savings', { amount: formatUsd(pricing.yearlySavings) })}</p>}
                <a className="pricing-v2-primary-cta" href="https://workspace.adcast.app/auth/signup" target="_blank" rel="noopener noreferrer">{t('redesign.primary_cta')} <span aria-hidden="true">→</span></a>
                <p className="pricing-v2-cta-note">{t('redesign.no_card_required')} <span aria-hidden="true">•</span> {t('redesign.cancel_anytime')}</p>
                <div className="pricing-v2-benefits-divider" />
                <ul className="pricing-v2-included-benefits">{PLAN_BENEFITS.map((key) => <li key={key}><i className="fas fa-check" aria-hidden="true" />{t(key)}</li>)}</ul>
                <div className="pricing-v2-store-buttons">
                  <a target="_blank" rel="noopener noreferrer" href={getAppDownloadUrl('ios', activeLang)} onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('ios', activeLang, getAffiliateCodeFromCurrentUrl()); }}><img src="/assets/icons/app_store.svg" alt={t('redesign.app_store_alt')} width="180" height="54" /></a>
                  <a target="_blank" rel="noopener noreferrer" href={getAppDownloadUrl('android', activeLang)} onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('android', activeLang, getAffiliateCodeFromCurrentUrl()); }}><img src="/assets/icons/play_store.svg" alt={t('redesign.google_play_alt')} width="180" height="54" /></a>
                </div>
              </article>

              <article className="pricing-v2-features-card">
                <span className="pricing-v2-feature-mark" aria-hidden="true">✦</span>
                <h2>{t('redesign.features_title')}</h2><p>{t('redesign.features_description')}</p>
                <ul className="pricing-v2-feature-list">{FEATURE_ROWS.map(([icon, title, description]) => (
                  <li key={title}><span className={`pricing-v2-feature-icon pricing-v2-feature-icon--${icon}`}><i className={`fas fa-${icon}`} aria-hidden="true" /></span><div><strong>{t(title)}</strong><small>{t(description)}</small></div></li>
                ))}</ul>
                <LangLink className="pricing-v2-features-link" to="/#main_features">{t('redesign.see_all_features')} <span aria-hidden="true">→</span></LangLink>
              </article>
            </div>

            <section className="pricing-v2-trust-row" aria-label={t('redesign.trust_label')}>
              {TRUST_ITEMS.map(([icon, title, description]) => <div key={title}><i className={`fas fa-${icon}`} aria-hidden="true" /><p><strong>{t(`redesign.${title}`)}</strong><span>{t(`redesign.${description}`)}</span></p></div>)}
            </section>

            <div className="pricing-v2-legal">
              <p><Trans i18nKey="pricing:pricing_disclaimer_p1" components={[<LangLink to="/terms" />, <LangLink to="/privacy-policy" />, <LangLink to="/refund-policy" />, <LangLink to="/support" />]} /></p>
              <p><Trans i18nKey="pricing:pricing_disclaimer_p2" components={[<ScrollLink to="mailto:help@adcast.app" />]} /></p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
